import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getSupportedBrokers,
  importTransactions,
} from "@/services/import-service";
import { toast } from "sonner";
import {
  FileUp,
  Loader2,
  Download,
  ChevronDown,
  PlayCircle,
} from "lucide-react";
import { ImportResult, TWRMode, Book } from "@/schemas";
import { ImportModeWarningModal } from "./import-mode-warning-modal";
import { normalizeBookType, shouldShowBookMode } from "@/lib/book-types";
import { ImportRecapModal } from "./import-recap-modal";
import { FeedbackModal } from "./feedback-modal";
import { getBooks } from "@/features/portfolio/services/portfolio-service";
import { EXCHANGE_LABELS } from "@/lib/exchange-mapping";
import { CURRENCY_NAMES } from "@/lib/currency";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface ImportTransactionsModalProps {
  onSuccess?: () => void | Promise<void>;
  book_id: string;
}

const BROKER_LABELS: Record<string, string> = {
  trade_republic: "Trade Republic",
  interactive_brokers: "Interactive Brokers",
  standard_csv: "Standard CSV",
  revolut: "Revolut",
};

const BROKER_TUTORIALS: Record<string, { embedUrl: string; watchUrl: string }> =
  {
    revolut: {
      embedUrl: "https://www.youtube-nocookie.com/embed/bqsaiY8hFoM",
      watchUrl: "https://youtu.be/bqsaiY8hFoM",
    },
    interactive_brokers: {
      embedUrl: "https://www.youtube-nocookie.com/embed/v5_3N8nfCNQ",
      watchUrl: "https://youtu.be/v5_3N8nfCNQ",
    },
  };

const STANDARD_CSV_HEADER = [
  "Date",
  "Type",
  "AssetIdentifier1",
  "Quantity1",
  "AssetIdentifier2",
  "Quantity2",
  "Amount",
  "Currency",
  "FeeAmount",
  "FeeCurrency",
  "Description",
  "ISIN",
  "Exchange",
].join(",");

const STANDARD_CSV_EXAMPLES = {
  simple: [
    [
      "2024-01-15",
      "BUY",
      "US0378331005",
      "10",
      "USD",
      "1500",
      "",
      "USD",
      "",
      "",
      "Buy AAPL (ISIN + Exchange)",
      "US0378331005",
      "US",
    ],
    [
      "2024-01-18",
      "BUY",
      "AAPL",
      "2",
      "USD",
      "300",
      "",
      "USD",
      "",
      "",
      "Buy AAPL (Ticker + Exchange)",
      "",
      "US",
    ],
    [
      "2024-01-22",
      "BUY",
      "US0378331005",
      "3",
      "EUR",
      "450",
      "",
      "EUR",
      "",
      "",
      "Buy AAPL (ISIN + Currency)",
      "US0378331005",
      "",
    ],
    [
      "2024-01-25",
      "BUY",
      "AAPL",
      "1",
      "EUR",
      "150",
      "",
      "EUR",
      "",
      "",
      "Buy AAPL (Ticker + Currency)",
      "",
      "",
    ],
    [
      "2024-02-01",
      "SELL",
      "US0378331005",
      "5",
      "USD",
      "800",
      "",
      "USD",
      "",
      "",
      "Sell AAPL (ISIN + Exchange)",
      "US0378331005",
      "US",
    ],
  ],
  advanced: [
    [
      "2024-01-15",
      "BUY",
      "US0378331005",
      "10",
      "USD",
      "1500",
      "",
      "USD",
      "",
      "",
      "Buy AAPL (ISIN + Exchange)",
      "US0378331005",
      "US",
    ],
    [
      "2024-01-18",
      "BUY",
      "AAPL",
      "2",
      "USD",
      "300",
      "",
      "USD",
      "",
      "",
      "Buy AAPL (Ticker + Exchange)",
      "",
      "US",
    ],
    [
      "2024-01-22",
      "BUY",
      "US0378331005",
      "3",
      "EUR",
      "450",
      "",
      "EUR",
      "",
      "",
      "Buy AAPL (ISIN + Currency)",
      "US0378331005",
      "",
    ],
    [
      "2024-01-25",
      "BUY",
      "AAPL",
      "1",
      "EUR",
      "150",
      "",
      "EUR",
      "",
      "",
      "Buy AAPL (Ticker + Currency)",
      "",
      "",
    ],
    [
      "2024-02-01",
      "SELL",
      "US0378331005",
      "5",
      "USD",
      "800",
      "",
      "USD",
      "",
      "",
      "Sell AAPL (ISIN + Exchange)",
      "US0378331005",
      "US",
    ],
    [
      "2024-01-10",
      "DEPOSIT",
      "EUR",
      "",
      "",
      "",
      "1000",
      "EUR",
      "",
      "",
      "Cash deposit",
      "",
      "",
    ],
    [
      "2024-01-20",
      "WITHDRAWAL",
      "EUR",
      "",
      "",
      "",
      "200",
      "EUR",
      "",
      "",
      "Cash withdrawal",
      "",
      "",
    ],
    [
      "2024-03-01",
      "DIVIDEND",
      "US0378331005",
      "",
      "",
      "",
      "5",
      "USD",
      "",
      "",
      "Dividend AAPL (ISIN + Exchange)",
      "US0378331005",
      "US",
    ],
  ],
};

type StandardCsvMode = "simple" | "advanced";

const EXCHANGE_LIST = Object.entries(EXCHANGE_LABELS)
  .map(([code, label]) => `${code} — ${label}`)
  .sort((a, b) => a.localeCompare(b));

const CURRENCY_LIST = Object.entries(CURRENCY_NAMES)
  .map(([code, name]) => `${code} — ${name}`)
  .sort((a, b) => a.localeCompare(b));

function formatBrokerLabel(broker: string): string {
  const knownLabel = BROKER_LABELS[broker];
  if (knownLabel) {
    return knownLabel;
  }
  return broker
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(" ");
}

function buildStandardCsvTemplate(mode: StandardCsvMode): string {
  const helpRow =
    "# HELP: AssetIdentifier1 = ticker/symbol. ISIN is optional but recommended. For BUY/SELL, AssetIdentifier2 is the payment currency/asset and Quantity2 is the total exchanged amount, not the unit price.";
  const rows = STANDARD_CSV_EXAMPLES[mode].map((row) => row.join(","));
  return [STANDARD_CSV_HEADER, helpRow, ...rows].join("\n");
}

function downloadStandardCsvTemplate(mode: StandardCsvMode): void {
  const content = buildStandardCsvTemplate(mode);
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `standard-csv-${mode}-template.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function buildUnsupportedBrokerFeedbackMessage(brokerName: string): string {
  const normalizedBrokerName =
    brokerName.trim() || "[please specify broker name]";

  return [
    "My broker is not supported for transaction import.",
    "",
    `Broker name: ${normalizedBrokerName}`,
    "",
    "I am attaching a sample export file from this broker.",
    "You can use it to evaluate support and test a future importer.",
    "",
  ].join("\n");
}

export function ImportTransactionsModal({
  onSuccess,
  book_id,
}: ImportTransactionsModalProps) {
  const [open, setOpen] = useState(false);
  const [brokers, setBrokers] = useState<string[]>([]);
  const [selectedBroker, setSelectedBroker] = useState<string>("");
  const [unsupportedBrokerName, setUnsupportedBrokerName] = useState("");
  const [isUnsupportedBrokerFeedbackOpen, setIsUnsupportedBrokerFeedbackOpen] =
    useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [portfolioMode, setPortfolioMode] = useState<TWRMode>("simple");
  const [portfolioType, setPortfolioType] = useState<
    Book["book_type"] | undefined
  >(undefined);
  const [isListsOpen, setIsListsOpen] = useState(false);

  // Modal states
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [showModeWarning, setShowModeWarning] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [pendingResult, setPendingResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const [supportedBrokers, books] = await Promise.all([
          getSupportedBrokers(),
          getBooks(),
        ]);
        setBrokers(supportedBrokers);

        // Find the current book to get its type and calculation mode
        const currentBook = books.find((b: Book) => b.book_id === book_id);
        setPortfolioType(currentBook?.book_type);
        if (currentBook?.twr_mode) {
          setPortfolioMode(currentBook.twr_mode);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        toast.error("Error", {
          description: "Could not fetch the list of supported brokers.",
        });
      }
    }

    if (open) {
      fetchData();
    }
  }, [open, book_id]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const selectedFile = event.target.files[0];
      const maxFileSizeBytes = 5 * 1024 * 1024; // 5MB

      if (selectedFile.size > maxFileSizeBytes) {
        toast.error("File Too Large", {
          description: `File size exceeds maximum allowed size: ${(selectedFile.size / (1024 * 1024)).toFixed(2)}MB (max: 5MB)`,
        });
        event.target.value = ""; // Reset file input
        setFile(null);
        return;
      }

      setFile(selectedFile);
    }
  };

  const handleImport = async () => {
    if (!selectedBroker || !book_id || !file) {
      toast.error("Missing Information", {
        description: "Please select a broker and choose a file.",
      });
      return;
    }

    setIsImporting(true);
    try {
      const result = await importTransactions(selectedBroker, book_id, file);
      setImportResult(result);

      // Check if we need to show mode warning
      if (
        shouldShowBookMode(portfolioType) &&
        result.detected_mode === "advanced" &&
        portfolioMode === "simple"
      ) {
        setPendingResult(result);
        setShowModeWarning(true);
        setOpen(false);
      } else if (result.missing_assets.length > 0 || result.errors.length > 0) {
        // Show recap modal if there are issues
        setShowRecap(true);
        setOpen(false);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        // Simple success case
        toast.success("Import Successful", {
          description: `${result.transactions_imported} transactions have been imported.`,
        });
        if (onSuccess) {
          onSuccess();
        }
        setOpen(false);
      }
    } catch (error: unknown) {
      console.error("Failed to import transactions:", error);
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred.";
      toast.error("Import Failed", {
        description: errorMessage,
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleUpgradeMode = () => {
    // TODO: Call API to upgrade portfolio mode
    toast.info("Mode Upgrade", {
      description: "Portfolio mode upgrade functionality coming soon.",
    });
    setShowModeWarning(false);
    if (pendingResult) {
      if (
        pendingResult.missing_assets.length > 0 ||
        pendingResult.errors.length > 0
      ) {
        setImportResult(pendingResult);
        setShowRecap(true);
      } else {
        toast.success("Import Successful", {
          description: `${pendingResult.transactions_imported} transactions have been imported.`,
        });
      }
      if (onSuccess) {
        onSuccess();
      }
    }
    setPendingResult(null);
  };

  const handleContinueAnyway = () => {
    setShowModeWarning(false);
    if (pendingResult) {
      if (
        pendingResult.missing_assets.length > 0 ||
        pendingResult.errors.length > 0
      ) {
        setImportResult(pendingResult);
        setShowRecap(true);
      } else {
        toast.success("Import Successful", {
          description: `${pendingResult.transactions_imported} transactions have been imported (BUY/SELL only).`,
        });
      }
      if (onSuccess) {
        onSuccess();
      }
    }
    setPendingResult(null);
  };

  const handleCancelModeWarning = () => {
    setShowModeWarning(false);
    setPendingResult(null);
  };

  const handleCloseRecap = () => {
    setShowRecap(false);
    setImportResult(null);
  };

  const selectedBrokerTutorial = selectedBroker
    ? BROKER_TUTORIALS[selectedBroker]
    : undefined;

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="h-10 border-muted/60">
            <FileUp className="mr-2 h-4 w-4" />
            Import
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              Import Transactions
            </DialogTitle>
            <DialogDescription>
              Select your broker and upload the CSV/Export file.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-6">
            <div className="space-y-2">
              <Label htmlFor="broker">Broker / Platform</Label>
              <Select onValueChange={setSelectedBroker} value={selectedBroker}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a broker" />
                </SelectTrigger>
                <SelectContent id="broker">
                  {brokers.map((broker) => (
                    <SelectItem
                      key={broker}
                      value={broker}
                      className="capitalize"
                    >
                      {formatBrokerLabel(broker)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!selectedBroker && (
                <div className="mt-4 space-y-3 rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 p-4">
                  <p className="text-sm font-medium text-foreground">
                    Your broker is not listed?
                  </p>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Help us add support by sending us your broker name and one
                    export example. We will use it to test and build a dedicated
                    importer.
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Input
                      value={unsupportedBrokerName}
                      onChange={(event) =>
                        setUnsupportedBrokerName(event.target.value)
                      }
                      placeholder="Broker name (optional)"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      className="shrink-0"
                      onClick={() => setIsUnsupportedBrokerFeedbackOpen(true)}
                    >
                      Send broker export
                    </Button>
                  </div>
                </div>
              )}
              {selectedBrokerTutorial && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    How to export from {formatBrokerLabel(selectedBroker)}
                  </p>
                  <div className="overflow-hidden rounded-lg border border-muted/60 bg-background">
                    <div className="relative w-full pt-[56.25%]">
                      <iframe
                        title={`${formatBrokerLabel(selectedBroker)} export tutorial`}
                        src={selectedBrokerTutorial.embedUrl}
                        className="absolute inset-0 h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    </div>
                  </div>
                  <a
                    href={selectedBrokerTutorial.watchUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex text-xs text-primary hover:underline"
                  >
                    Open tutorial on YouTube
                  </a>
                </div>
              )}
              {selectedBroker === "trade_republic" && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm font-medium text-foreground">
                    Trade Republic export demo
                  </p>
                  <div className="flex aspect-video items-center justify-center rounded-lg border border-muted/60 bg-muted/30">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <PlayCircle className="h-9 w-9" aria-hidden="true" />
                      <p className="text-sm font-medium">
                        Demo video coming soon
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {selectedBroker === "standard_csv" && (
                <div className="mt-4 space-y-4">
                  {/* Download Templates Section */}
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-sm text-muted-foreground">
                      Download CSV template
                    </p>
                    <div className="flex gap-2 w-full max-w-xs">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => downloadStandardCsvTemplate("simple")}
                      >
                        <Download className="mr-2 h-3.5 w-3.5" />
                        Simple
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => downloadStandardCsvTemplate("advanced")}
                      >
                        <Download className="mr-2 h-3.5 w-3.5" />
                        Advanced
                      </Button>
                    </div>
                  </div>

                  {/* Help Card */}
                  <div className="rounded-lg border border-muted/60 bg-muted/30 divide-y divide-muted/60">
                    {/* Section 1: Intro */}
                    <div className="px-4 py-3">
                      <h4 className="text-sm font-semibold text-foreground mb-1">
                        CSV Format Guide
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        Use the templates above to ensure correct formatting.
                        The <strong># HELP</strong> line in templates is ignored
                        by the importer.
                      </p>
                    </div>

                    {/* Section 2: How to fill */}
                    <div className="px-4 py-3">
                      <h4 className="text-sm font-semibold text-foreground mb-2">
                        How to fill the CSV
                      </h4>
                      <ul className="space-y-1.5 text-xs text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <span className="text-foreground font-medium">•</span>
                          <span>
                            For <strong>BUY/SELL/DIVIDEND</strong>, set{" "}
                            <strong>AssetIdentifier1</strong> (ticker or ISIN).
                            If <strong>Exchange</strong> is provided, it is
                            combined with the identifier. If Exchange is empty,
                            use <strong>Currency</strong> (or AssetIdentifier2
                            for BUY/SELL) to disambiguate.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-foreground font-medium">•</span>
                          <span>
                            For <strong>BUY/SELL</strong>,{" "}
                            <strong>AssetIdentifier2</strong> is the cash
                            currency and <strong>Quantity2</strong> is the total
                            amount.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-foreground font-medium">•</span>
                          <span>
                            For <strong>DEPOSIT/WITHDRAWAL</strong>,{" "}
                            <strong>AssetIdentifier1</strong> must be a currency
                            code and must match <strong>Currency</strong>.
                          </span>
                        </li>
                      </ul>
                    </div>

                    {/* Section 3: Supported exchanges/currencies (Collapsible) */}
                    <Collapsible
                      open={isListsOpen}
                      onOpenChange={setIsListsOpen}
                    >
                      <CollapsibleTrigger className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors">
                        <h4 className="text-sm font-semibold text-foreground">
                          Supported exchanges & currencies
                        </h4>
                        <ChevronDown
                          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isListsOpen ? "rotate-180" : ""}`}
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="px-4 pb-3 space-y-3">
                        <div>
                          <div className="text-xs font-medium text-foreground mb-1.5">
                            Exchanges
                          </div>
                          <div className="max-h-32 overflow-auto rounded-md border border-muted/60 bg-background px-3 py-2 text-xs text-muted-foreground">
                            <div className="flex flex-wrap gap-x-2 gap-y-1">
                              {EXCHANGE_LIST.map((item, idx) => (
                                <span key={idx} className="whitespace-nowrap">
                                  {item}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-foreground mb-1.5">
                            Currencies
                          </div>
                          <div className="max-h-32 overflow-auto rounded-md border border-muted/60 bg-background px-3 py-2 text-xs text-muted-foreground">
                            <div className="flex flex-wrap gap-x-2 gap-y-1">
                              {CURRENCY_LIST.map((item, idx) => (
                                <span key={idx} className="whitespace-nowrap">
                                  {item}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="file">Export File (CSV/PDF)</Label>
              <Input
                id="file"
                type="file"
                onChange={handleFileChange}
                className="cursor-pointer"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={isImporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={isImporting || !selectedBroker || !file}
            >
              {isImporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                "Start Import"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mode Warning Modal */}
      {pendingResult && (
        <ImportModeWarningModal
          open={showModeWarning}
          onOpenChange={setShowModeWarning}
          detectedMode={pendingResult.detected_mode || "advanced"}
          portfolioMode={portfolioMode}
          portfolioType={normalizeBookType(portfolioType)}
          onUpgradeMode={handleUpgradeMode}
          onContinueAnyway={handleContinueAnyway}
          onCancel={handleCancelModeWarning}
        />
      )}

      {/* Import Recap Modal */}
      {importResult && (
        <ImportRecapModal
          open={showRecap}
          onOpenChange={setShowRecap}
          result={importResult}
          onClose={handleCloseRecap}
        />
      )}
      <FeedbackModal
        open={isUnsupportedBrokerFeedbackOpen}
        onOpenChange={setIsUnsupportedBrokerFeedbackOpen}
        initialCategory="feature"
        initialMessage={buildUnsupportedBrokerFeedbackMessage(
          unsupportedBrokerName,
        )}
      />
    </>
  );
}
